import React, { createContext, useContext, useEffect, useRef } from "react";
import {
  onValue,
  ref,
  update,
  onDisconnect,
  serverTimestamp as rtdbServerTimestamp,
} from "firebase/database";
import { rdb } from "@/services/config";
import { useUser } from "@/contexts";

type PresenceContextType = {};

const PresenceContext = createContext<PresenceContextType>({});

export const PresenceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useUser();

  const disconnectRef = useRef<any>(null);
  const activityCleanup = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!user?.uid) return;

    const uid = user.uid;
    const userStatusRef = ref(rdb, `status/${uid}`);
    const connectedRef = ref(rdb, ".info/connected");

    const unsub = onValue(connectedRef, async (snap) => {
      if (!snap.val()) return;

      const offlineState = {
        state: "offline",
        lastChanged: rtdbServerTimestamp(),
      };

      const onlineState = {
        state: "online",
        lastChanged: rtdbServerTimestamp(),
        lastActivity: rtdbServerTimestamp(),
      };

      await disconnectRef.current?.cancel?.();

      disconnectRef.current = onDisconnect(userStatusRef);
      await disconnectRef.current.set(offlineState);

      await update(userStatusRef, onlineState);
    });

    let idleTimer: any;
    let lastUpdate = 0;

    const handleActivity = async () => {
      clearTimeout(idleTimer);

      const now = Date.now();

      if (now - lastUpdate > 60000) {
        lastUpdate = now;

        await update(userStatusRef, {
          lastActivity: rtdbServerTimestamp(),
          state: "online",
        });
      }

      idleTimer = setTimeout(
        async () => {
          await update(userStatusRef, { state: "away" });
        },
        5 * 60 * 1000,
      );
    };

    const events = [
      "mousemove",
      "keydown",
      "mousedown",
      "touchstart",
      "scroll",
    ];

    events.forEach((e) =>
      window.addEventListener(e, handleActivity, { passive: true }),
    );

    handleActivity();

    activityCleanup.current = () => {
      clearTimeout(idleTimer);
      events.forEach((e) => window.removeEventListener(e, handleActivity));
    };

    return () => {
      unsub();
      disconnectRef.current?.cancel?.();
      activityCleanup.current?.();
    };
  }, [user?.uid]);

  return (
    <PresenceContext.Provider value={{}}>{children}</PresenceContext.Provider>
  );
};

export const usePresence = () => useContext(PresenceContext);
